import { Inject, Injectable, NgZone } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';

import { debounce } from 'lodash';

import { AppConfigService } from '@app/src/app/minimal/services/AppConfig';
import { SentryService } from '@app/src/app/minimal/services/Sentry/Sentry.service';
import { HomeRoute } from '@app/src/app/modules/home/constants/home.constants';

import { AppRuntime, AppRuntimeInterface } from '../app-runtime';
import { isRosterstreamSSOEnabled } from '../auth/sso/sso.utils';
import { LINKS_WHITELIST } from './branch.constants';
import { BranchLinkData } from './branch.types';

declare var Branch: any;

@Injectable({
  providedIn: 'root',
})
export class BranchService {
  private initialized = false;
  /**
   * Debounce this handler in case both branch and handleOpenURL try to handle the same link
   */
  private debouncedHandleDeepLink = debounce(this.handleDeepLink.bind(this), 300);

  constructor(
    @Inject(AppRuntimeInterface) private runtime: AppRuntime,
    private router: Router,
    private zone: NgZone,
    private sentry: SentryService,
    private appConfigService: AppConfigService,
  ) {
    // Adding this here because it seems like Branch `Branch.initSession()` doesn't always capture the incoming link
    (<any>window).handleOpenURL = async (url: string) => {
      // legacy sso is still relying on this same handler in the auth service so we only want to process Beta sso callbacks
      if (!isRosterstreamSSOEnabled(this.appConfigService.getValue('rosterStreamSSOEnabled'))) return;
      try {
        this.disableTracking();
        // Force Branch to re-initialize with this URL
        const branchData = await Branch.initSession();
        console.log('Branch SDK parsed data:', JSON.stringify(branchData, null, 2));
        this.debouncedHandleDeepLink(branchData);
      } catch (error) {
        this.sentry.captureMessageAsError(`Failed to parse Branch data on handleOpenUrl: ${url}`, { error });
      }
    };
  }

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    // if it's web lets skip this completely
    if (!this.runtime.isNativeApp()) {
      return;
    }

    try {
      if (typeof Branch === 'undefined') {
        console.warn('Branch SDK not available');
        return;
      }

      this.disableTracking();
      const data: any = await Branch.initSession();
      console.log('Branch SDK parsed data on initialization:', JSON.stringify(data, null, 2));
      this.debouncedHandleDeepLink(data);

      // Set up resume listener for when app comes back from background
      this.setupResumeListener();

      this.initialized = true;
    } catch (error) {
      this.sentry.captureMessageAsError('Failed to initialize Branch', { error });
    }
  }

  private setupResumeListener(): void {
    document.addEventListener('resume', async () => {
      try {
        this.disableTracking();
        const data: any = await Branch.initSession();
        console.log('App resumed, Branch data:', JSON.stringify(data));
        this.debouncedHandleDeepLink(data);
      } catch (error) {
        this.sentry.captureMessageAsError('Failed to re-initialize Branch on resume', { error });
      }
    });
  }

  private async handleDeepLink(data: any): Promise<void> {
    // Branch data can be nested in the 'data' property or at the top level
    const linkData: BranchLinkData = data.data || data;

    // Check if this data actually contains a Branch link click
    if (!linkData?.['+clicked_branch_link']) {
      return;
    }

    console.log('Handling Branch deep link data:', JSON.stringify(linkData));

    // routing
    const incomingRoute = (linkData.route as string) || linkData.$deeplink_path;
    const isWhitelistedRoute = LINKS_WHITELIST.some(whitelistedTopLevelRoute =>
      incomingRoute.startsWith(whitelistedTopLevelRoute),
    );
    const route = isWhitelistedRoute ? incomingRoute : `/${HomeRoute.ROOT}`;

    // Build query params from any additional data
    const navigationOptions: NavigationExtras = {};

    if (isWhitelistedRoute) {
      // Exclude Branch-specific fields and prefixes from query params
      const EXCLUDED_PREFIXES = ['$', '+', '~'];
      const queryParams: Record<string, any> = Object.fromEntries(
        Object.entries(linkData).filter(([key]) => !EXCLUDED_PREFIXES.some(prefix => key.startsWith(prefix))),
      );
      if (Object.keys(queryParams).length > 0) {
        navigationOptions.queryParams = queryParams;
      }
    }

    try {
      this.zone.run(async () => {
        await this.router.navigate([route], navigationOptions);
      });
    } catch (error) {
      this.sentry.captureMessageAsError('Failed to navigate to Branch route', { error, route, linkData });
    }
  }

  private disableTracking(): void {
    if (this.appConfigService.getValue('disableBranchTracking')) {
      Branch.disableTracking(true);
    }
  }
}
